/**
 * Typed wrapper for the self-service "My Profile" read (UX-21).
 *
 * Path + shape taken from the backend's GET /api/v1/me/profile route
 * (RequireAuth; strict OWN scope — the server derives the employee from
 * the session, never from a body/param). Cookie-only auth + CSRF +
 * 401→refresh are all handled by the shared axios instance
 * (lib/api/client.ts).
 *
 * Read-only surface — no mutation wrappers. Corrections go through the
 * existing change-requests flow.
 */

import { api } from "./client";
import type { MeProfile } from "@/types/me-profile";

export const ME_PROFILE_BASE = "/api/v1/me/profile";

/** GET /me/profile — the caller's own personal + employment details. */
export async function getMyProfile(signal?: AbortSignal): Promise<MeProfile> {
  const { data } = await api.get<MeProfile>(ME_PROFILE_BASE, { signal });
  return data;
}

/**
 * The low-risk fields an employee may edit directly (no HR approval).
 * Omit a key to leave it alone; "" clears it. Everything else — name, DOB,
 * nominee, address, bank — goes through change requests.
 */
export interface UpdateMyProfileInput {
  phone?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
}

/** PATCH /me/profile — returns the refreshed profile. */
export async function updateMyProfile(
  body: UpdateMyProfileInput,
): Promise<MeProfile> {
  const { data } = await api.patch<MeProfile>(ME_PROFILE_BASE, body);
  return data;
}
